// 永佳捷電梯管理 API

/**
 * @function ApiClient#GetElevatorOverview
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} options = {}
 */
export async function GetElevatorOverview(cid, options = {}) {
  return await this._get(`/community/${cid}/elevator/overview`, options);
}

/**
 * 以後端搜尋與分頁取得所有戶別的永佳捷叫梯設定。
 * @param {string} cid 社區 ID。
 * @param {object} query 搜尋與分頁參數。
 * @param {object} options API 選項。
 */
export async function GetElevatorHouseholdCallSettingList(cid, query = {}, options = {}) {
  const params = new URLSearchParams();
  if (query.page) params.set("page", query.page);
  if (query.pp) params.set("pp", query.pp);
  if (query.search) params.set("search", query.search);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return await this._get(`/community/${cid}/elevator/households/call-settings${suffix}`, options);
}

/**
 * 對多個戶別批次套用同一組永佳捷叫梯設定。
 * @param {string} cid 社區 ID。
 * @param {object} data 戶別清單、服務區段、樓層與啟用狀態。
 * @param {object} options API 選項。
 */
export async function BatchSaveElevatorHouseholdCallSettings(cid, data, options = {}) {
  return await this._put(`/community/${cid}/elevator/households/call-settings/batch`, data, options);
}

/**
 * 取得指定戶別的永佳捷叫梯設定。
 * @param {string} cid 社區 ID。
 * @param {string} householdId 戶別 ID。
 * @param {object} options API 選項。
 */
export async function GetElevatorHouseholdCallSetting(cid, householdId, options = {}) {
  return await this._get(`/community/${cid}/elevator/households/${householdId}/call-setting`, options);
}

/**
 * 儲存指定戶別的永佳捷叫梯設定。
 * @param {string} cid 社區 ID。
 * @param {string} householdId 戶別 ID。
 * @param {object} data 服務區段、樓層與啟用狀態。
 * @param {object} options API 選項。
 */
export async function SaveElevatorHouseholdCallSetting(cid, householdId, data, options = {}) {
  return await this._put(`/community/${cid}/elevator/households/${householdId}/call-setting`, data, options);
}

/**
 * 移除指定戶別的永佳捷叫梯設定。
 * @param {string} cid 社區 ID。
 * @param {string} householdId 戶別 ID。
 * @param {object} options API 選項。
 */
export async function DeleteElevatorHouseholdCallSetting(cid, householdId, options = {}) {
  return await this._delete(`/community/${cid}/elevator/households/${householdId}/call-setting`, options);
}

/**
 * @function ApiClient#GetElevatorConnectionOptions
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} options = {}
 */
export async function GetElevatorConnectionOptions(cid, options = {}) {
  return await this._get(`/community/${cid}/elevator/connection-options`, options);
}

/** 即時讀取指定電梯的載客狀態、設備健康與人數。 */
export async function GetElevatorDetector(cid, id, options = {}) {
  return await this._get(`/community/${cid}/elevator/${id}/detector`, { timeout: 20000, retry: 0, ...options });
}

/** 經明確確認後單次清除偵測器目前與累計人數，不自動重試。 */
export async function ClearElevatorDetectorCount(cid, id, options = {}) {
  return await this._post(`/community/${cid}/elevator/${id}/detector/count/clear`, { Confirmed: true }, { timeout: 20000, retry: 0, ...options });
}

/**
 * @function ApiClient#AddElevator
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} data
 * @param {any} options = {}
 */
export async function AddElevator(cid, data, options = {}) {
  return await this._post(`/community/${cid}/elevator`, data, options);
}

/**
 * @function ApiClient#UpdateElevator
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} data
 * @param {any} options = {}
 */
export async function UpdateElevator(cid, id, data, options = {}) {
  return await this._put(`/community/${cid}/elevator/${id}`, data, options);
}

/**
 * @function ApiClient#DeleteElevator
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} options = {}
 */
export async function DeleteElevator(cid, id, options = {}) {
  return await this._delete(`/community/${cid}/elevator/${id}`, options);
}

/**
 * @function ApiClient#AddElevatorController
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} data
 * @param {any} options = {}
 */
export async function AddElevatorController(cid, data, options = {}) {
  return await this._post(`/community/${cid}/elevator/controller`, data, options);
}

/**
 * @function ApiClient#UpdateElevatorController
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} data
 * @param {any} options = {}
 */
export async function UpdateElevatorController(cid, id, data, options = {}) {
  return await this._put(`/community/${cid}/elevator/controller/${id}`, data, options);
}

/**
 * @function ApiClient#DeleteElevatorController
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} options = {}
 */
export async function DeleteElevatorController(cid, id, options = {}) {
  return await this._delete(`/community/${cid}/elevator/controller/${id}`, options);
}

/**
 * @function ApiClient#GetElevatorControllerSnapshot
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} options = {}
 */
export async function GetElevatorControllerSnapshot(cid, id, options = {}) {
  return await this._get(`/community/${cid}/elevator/controller/${id}/snapshot`, options);
}

/**
 * @function ApiClient#CallElevator
 * @this {ApiClient}
 * @param {any} cid
 * @param {any} id
 * @param {any} data
 */
export async function CallElevator(cid, id, data) {
  return await this._post(`/community/${cid}/elevator/controller/${id}/call`, data, {
    retry: 0,
    isSuccess: (response) => response?.status === 202,
  });
}
